import { FC, useMemo } from 'react';
import { sanitizeHtml } from '@new/common/lib/sanitize';
import { type CourseWithProgress } from '@teachur/http-client';
import { ExampleFileLink } from '@new/common/ui/ExampleFileLink/ExampleFileLink';
import { FileSection } from '@new/features/lessons/components/FileSection/FileSection';
import { mapFileToItem } from '@new/features/lessons/lib/fileDataHelper';

interface CourseProjectTabProps {
  course: CourseWithProgress;
}

export const CourseProjectTab: FC<CourseProjectTabProps> = ({ course }) => {
  const projects = course.projects || [];

  return (
    <div className="flex flex-col gap-8">
      {projects.map((project) => {
        const projectFiles = useMemo(
          () => project.files?.map((file) => mapFileToItem(file)).filter((file) => !!file) || [],
          [project.files]
        );

        const handleDownloadFile = (url: string) => {
          window.open(url, '_blank');
        };

        return (
          <div
            key={project.id}
            className="flex flex-col gap-4 rounded-xl bg-(--neutral-white-NW1000) border border-(--neutral-black-NB200) shadow-sm p-6"
          >
            <h3 className="m-0! [font-size:var(--fs-h6)]! lg:[font-size:var(--fs-h5)]! font-bold text-(--neutral-black-NB1000)!">
              {project.name}
            </h3>
            {project.description && (
              <div className="flex flex-col gap-2">
                <h5 className="m-0! [font-size:var(--fs-l3)]! lg:[font-size:var(--fs-h6)]! font-bold text-(--neutral-black-NB1000)!">
                  Project description
                </h5>
                <div
                  className="flex max-w-full flex-col gap-3 overflow-hidden whitespace-normal [font-size:var(--fs-p4)]! lg:[font-size:var(--fs-p3)]! leading-[150%] text-(--neutral-black-NB900)! **:max-w-full **:whitespace-normal **:wrap-break-word [&_ol]:m-0! [&_p]:m-0! [&_ul]:m-0!"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(project.description),
                  }}
                />
              </div>
            )}

            {!!project.goals?.length && (
              <div className="flex flex-col gap-2">
                <h5 className="m-0! [font-size:var(--fs-l3)]! lg:[font-size:var(--fs-h6)]! font-bold text-(--neutral-black-NB1000)!">
                  Project goals
                </h5>

                <ul className="m-0! flex flex-col gap-2 p-0!">
                  {project.goals.map((goal) => (
                    <li
                      key={goal.id}
                      className="flex items-center gap-3 [font-size:var(--fs-p4)]! lg:[font-size:var(--fs-p3)]! leading-[150%] text-(--neutral-black-NB900)!"
                    >
                      <span className="h-3.5 w-3.5 shrink-0 bg-(--secondary-B200)" />
                      {goal.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <FileSection
              title="Project files"
              files={projectFiles}
              onDownload={handleDownloadFile}
              showStatus={false}
            />

            {project.exampleFileUrl && <ExampleFileLink url={project.exampleFileUrl} />}
          </div>
        );
      })}
    </div>
  );
};
